import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, serverTimestamp, query, where, getDoc, documentId, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { LINE_TYPES } from '../firebase/schema';
import { getMockupUrl, getPersistableMockupUrl, normalizeFilesForFirestore } from '../utils/fileHelpers';
import LineItem2 from '../components/LineItem2';
import chroma from 'chroma-js';
import pantoneColors from 'pantone-colors';
import HistoryIcon from '@mui/icons-material/History';

const PageContainer = styled.div`
  padding: 20px;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  margin-top: 80px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Button = styled.button`
  background-color: #62E07D;
  color: black;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;

  &:hover {
    background-color: #50B366;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  text-transform: uppercase;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid ${props => props.theme.colors.border};
  position: sticky;
  top: 0;
  background: ${props => props.theme.colors.background};
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  user-select: none;
  
  &:hover {
    background: ${props => props.sortable ? 'rgba(98, 224, 125, 0.1)' : props.theme.colors.background};
  }
  
  ${props => props.active && `
    color: #62E07D;
  `}
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ColorSwatch = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  border: 2px solid ${props => props.theme.colors.border};
  background-color: ${props => props.color || '#ccc'};
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
`;

const Tr = styled.tr`
  &:hover {
    background: ${props => props.theme.colors.hover};
  }
`;

const EditButton = styled.button`
  background-color: #62E07D;
  color: black;
  border: none;
  padding: 5px 10px;
  border-radius: 0;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  margin-right: 8px;
  
  &:hover {
    background-color: #50B366;
  }
`;

const DeleteButton = styled.button`
  background-color: #FF4C4C;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 0;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  
  &:hover {
    background-color: #CC3D3D;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--al-bg);
  padding: 30px;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    color: white;
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    margin-bottom: 30px;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  background: white;
  color: black;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #62E07D;
  }

  &::placeholder {
    color: #666;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  background: white;
  color: black;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

const CancelButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background: #444;
  }
`;

const SaveButton = styled.button`
  background: #62E07D;
  color: black;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background: #50B366;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
  width: 50%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.9em;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.text};
    opacity: 0.6;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 8px;
  background: var(--al-bg);
  padding: 4px;
  border-radius: 4px;
  border: 1px solid #333;
`;

const ViewButton = styled.button`
  background: ${props => props.active ? '#62E07D' : 'transparent'};
  color: ${props => props.active ? 'black' : 'white'};
  border: none;
  padding: 8px 16px;
  border-radius: 2px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#50B366' : 'rgba(98, 224, 125, 0.2)'};
  }
`;

const MatcherContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  min-height: 600px;
`;

const MatcherInputSection = styled.div`
  background: var(--al-bg);
  border: 1px solid #333;
  border-radius: 8px;
  padding: 32px;
  width: 100%;
  max-width: 600px;
  margin-bottom: 40px;
`;

const MatcherTitle = styled.h2`
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 24px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 24px;
  text-align: center;
`;

const HexInputGroup = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
`;

const HexInputWrapper = styled.div`
  flex: 1;
`;

const ColorPreview = styled.div`
  width: 70px;
  height: 38px;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.color || '#333'};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
`;

const EyedropperButton = styled.button`
  background: rgba(98, 224, 125, 0.2);
  color: #62E07D;
  border: 1px solid #62E07D;
  padding: 12px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(98, 224, 125, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const MatchResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1400px;
`;

const MatchCard = styled.div`
  background: var(--al-bg);
  border: 2px solid ${props => {
    if (props.deltaE < 5) return '#62E07D'; // Excellent match
    if (props.deltaE < 15) return '#FFD700'; // Good match
    if (props.deltaE < 30) return '#FFA500'; // Moderate match
    return '#FF6B6B'; // Poor match
  }};
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  }
`;

const MatchCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const MatchColorSwatch = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background-color: ${props => props.color || '#ccc'};
  border: 3px solid ${props => props.theme.colors.border};
  flex-shrink: 0;
`;

const MatchInfo = styled.div`
  flex: 1;
`;

const MatchName = styled.div`
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const MatchDetails = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  line-height: 1.6;
`;

const DeltaEBadge = styled.div`
  background: ${props => {
    if (props.value < 5) return '#62E07D';
    if (props.value < 15) return '#FFD700';
    if (props.value < 30) return '#FFA500';
    return '#FF6B6B';
  }};
  color: black;
  padding: 8px 16px;
  border-radius: 20px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
`;

const MatchQuality = styled.div`
  color: ${props => {
    if (props.deltaE < 5) return '#62E07D';
    if (props.deltaE < 15) return '#FFD700';
    if (props.deltaE < 30) return '#FFA500';
    return '#FF6B6B';
  }};
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  text-align: center;
  margin-top: 12px;
`;

const NoResults = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  text-transform: uppercase;
  text-align: center;
  padding: 60px 20px;
`;

const WheelContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  min-height: 900px;
  position: relative;
  overflow: auto;
`;

const Tooltip = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  pointer-events: none;
  z-index: 1000;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 600;
  border: 2px solid #62E07D;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  white-space: nowrap;
  
  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #62E07D;
  }
`;

const ColorStatsPanel = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(28, 28, 28, 0.95);
  border: 1px solid #333;
  border-radius: 8px;
  padding: 20px;
  min-width: 200px;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 11px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #333;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
  
  .label {
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
  }
  
  .value {
    color: #62E07D;
    font-weight: 700;
    font-size: 14px;
  }
  
  .bar {
    width: 100%;
    height: 6px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    margin-top: 6px;
    overflow: hidden;
  }
  
  .fill {
    height: 100%;
    background: #62E07D;
    transition: width 0.3s;
  }
`;

// Ink History Styled Components
const HistoryContainer = styled.div`
  padding: 20px 0;
`;

// Header row for Ink History — visually matches List/Wheel search bar row
const HistorySearchSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const HistorySearchTitle = styled.h2`
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
`;

const HistorySearchInputWrapper = styled.div`
  flex: 0 0 50%;
`;

const HistoryResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #333;
`;

const HistoryResultsCount = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
`;

const HistoryResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HistoryCard = styled.div`
  background: var(--al-bg);
  border: 1px solid #333;
  border-radius: 8px;
  padding: 0;
  transition: all 0.2s;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;

  &:hover {
    border-color: #62E07D;
    background: #222;
  }
`;

const HistoryMockupContainer = styled.div`
  width: 140px;
  height: 140px;
  flex-shrink: 0;
  align-self: flex-start;
  overflow: hidden;
  background: ${props => props.noImage ? '#333' : '#363636'};
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HistoryMockupImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HistoryCardBody = styled.div`
  flex: 1;
  min-width: 0;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HistoryCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const HistoryCardInfo = styled.div`
  flex: 1;
`;

const HistoryCardTitle = styled.div`
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const HistoryCardMeta = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const HistoryCardDate = styled.div`
  background: rgba(98, 224, 125, 0.15);
  color: #62E07D;
  padding: 6px 12px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
`;

const HistoryCardNotes = styled.div`
  background: var(--al-surface);
  border-left: 3px solid #62E07D;
  padding: 8px 12px;
  border-radius: 0 4px 4px 0;
`;

const HistoryCardNotesLabel = styled.div`
  color: #62E07D;
  font-family: 'Montserrat', sans-serif;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const HistoryCardNotesText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const HistoryCardInks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const HistoryInkChip = styled.span`
  background: ${props => props.isSearched ? 'rgba(98, 224, 125, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.isSearched ? '#62E07D' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => props.isSearched ? 'rgba(98, 224, 125, 0.4)' : 'transparent'};
  padding: 4px 10px;
  border-radius: 12px;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: ${props => props.isSearched ? '600' : '400'};
`;

const HistoryNoNotes = styled.div`
  color: rgba(255, 255, 255, 0.3);
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-style: italic;
`;

const HistoryEmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
`;

const HistoryLoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #62E07D;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
`;

// Edit Line Item modal (same as Lines page)
const EditLineModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const EditLineModalBox = styled.div`
  width: 95%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  background-color: var(--al-bg);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const EditLineModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: var(--al-surface);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const EditLineModalTitle = styled.h3`
  color: var(--al-primary);
  font-weight: 600;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
  font-size: 1.1rem;
  margin: 0;
`;

const EditLineCloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  &:hover { color: var(--al-primary); }
`;

const EditLineModalContent = styled.div`
  padding: 20px;
`;

const EditLineModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  background-color: var(--al-surface);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const EditLineCancelButton = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  padding: 10px 20px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  &:hover { color: white; border-color: white; }
`;

const EditLineSaveButton = styled.button`
  background: var(--al-primary);
  border: none;
  color: #222;
  padding: 10px 20px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  &:hover { background: #7FFF7F; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// Ink History Styled Components for Autocomplete
const HistoryAutocompleteWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const HistoryAutocompleteDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--al-surface);
  border: 1px solid #62E07D;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
`;

const HistoryAutocompleteItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  color: white;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    background: #62E07D;
    color: black;
  }
`;

const HistoryAutocompleteColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 3px;
  background: ${props => props.color || '#666'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

// Cap so Ink History load time stays bounded as order lines grow
const INK_HISTORY_LINE_LIMIT = 5000;

// Map DB type to LINE_TYPES for Edit Line modal (same as Lines.js)
const normalizePrintTypeForDisplay = (type) => {
  if (!type) return LINE_TYPES.SCREEN_PRINT;
  const t = String(type).toLowerCase();
  if (t === 'screen' || t === 'screen print') return LINE_TYPES.SCREEN_PRINT;
  if (t === 'embroidery') return LINE_TYPES.EMBROIDERY;
  if (t === 'dtg') return LINE_TYPES.DTG;
  if (t === 'dtf') return LINE_TYPES.DTF;
  if (t === 'design_only' || t === 'design only') return LINE_TYPES.DESIGN_ONLY;
  if (Object.values(LINE_TYPES).includes(type)) return type;
  return LINE_TYPES.SCREEN_PRINT;
};

const getSizeTypeForLine = (line) => {
  const sizes = line.sizes || {};
  const sizeKeys = Object.keys(sizes).filter(k => sizes[k] > 0);
  if (sizeKeys.some(s => s.startsWith('Y'))) return 'youth';
  if (sizeKeys.some(s => ['2', '3', '4', '5', '6', '7'].includes(s) || s.includes('T'))) return 'toddler';
  if (sizeKeys.some(s => ['NewBorn', 'NB', '3-6', '6-12', '12-18', '18-24'].includes(s))) return 'infant';
  if (sizeKeys.length === 1 && sizeKeys[0] === 'ONESIZE') return 'onesize';
  return line.sizeType || 'unisex';
};

// Ink History Component — type-to-search by ink name, client, or design (like Lines page)
const InkHistory = ({ inks }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [enrichedLines, setEnrichedLines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingLine, setEditingLine] = useState(null);
  const [editedLineData, setEditedLineData] = useState(null);
  const [savingLine, setSavingLine] = useState(false);

  // Load most recent lines with ink data + their orders once on mount
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const linesRef = collection(db, 'orderLines');
        const linesQuery = query(
          linesRef,
          orderBy('createdAt', 'desc'),
          limit(INK_HISTORY_LINE_LIMIT)
        );
        const linesSnapshot = await getDocs(linesQuery);
        const orderIds = new Set();
        const linesWithInk = [];

        linesSnapshot.docs.forEach(docSnap => {
          const line = { id: docSnap.id, ...docSnap.data() };
          const t = String(line.type || '').toLowerCase();
          const isScreenPrint = t === 'screen' || t === 'screen print';
          if (!isScreenPrint) return;
          const allInks = [
            ...(line.frontInkColors || []),
            ...(line.backInkColors || []),
            ...(line.otherInkColors || [])
          ].filter(Boolean);
          const hasInkData = allInks.length > 0 || (line.inkNotes && line.inkNotes.trim());
          if (hasInkData) {
            linesWithInk.push({ ...line, allInks });
            if (line.orderId) orderIds.add(line.orderId);
          }
        });

        // Batch fetch orders (Firestore 'in' allows max 30 IDs per query) in parallel
        const orderData = {};
        const orderIdArray = Array.from(orderIds);
        const BATCH_SIZE = 30;
        const batches = [];
        for (let i = 0; i < orderIdArray.length; i += BATCH_SIZE) {
          batches.push(orderIdArray.slice(i, i + BATCH_SIZE));
        }
        const ordersRef = collection(db, 'orders');
        const batchResults = await Promise.all(
          batches.map(batch =>
            getDocs(query(ordersRef, where(documentId(), 'in', batch)))
          )
        );
        batchResults.forEach(snap => {
          if (cancelled) return;
          snap.docs.forEach(d => {
            orderData[d.id] = d.data();
          });
        });

        if (cancelled) return;
        const enriched = linesWithInk.map(line => {
          const order = orderData[line.orderId] || {};
          return {
            ...line,
            orderNumber: order.orderNumber || line.orderId?.slice(-6) || 'N/A',
            customerName: order.customerName || 'Unknown Customer',
            orderDate: order.createdAt || line.createdAt,
            dueDate: order.dueDate
          };
        });
        enriched.sort((a, b) => {
          const dateA = a.orderDate?.toDate?.() || a.orderDate || new Date(0);
          const dateB = b.orderDate?.toDate?.() || b.orderDate || new Date(0);
          return dateB - dateA;
        });
        setEnrichedLines(enriched);
      } catch (error) {
        console.error('Error loading ink history:', error);
        setEnrichedLines([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  // Filter by ink name, client (customer), or design name — like Lines page, no button
  const filteredResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];
    return enrichedLines.filter(line => {
      const customerMatch = (line.customerName || '').toLowerCase().includes(term);
      const designMatch = (line.designName || line.design || '').toLowerCase().includes(term);
      const inkMatch = (line.allInks || []).some(ink => (ink || '').toLowerCase().includes(term));
      return customerMatch || designMatch || inkMatch;
    });
  }, [enrichedLines, searchTerm]);

  const openEditModal = (line) => {
    const lineItem2Data = {
      id: line.id,
      design: line.designName,
      designName: line.designName,
      type: normalizePrintTypeForDisplay(line.type),
      style: line.garmentId,
      color: line.garmentColor,
      sizeType: getSizeTypeForLine(line),
      sizes: line.sizes || {},
      price: line.pricePerPiece,
      pricePerPiece: line.pricePerPiece,
      cogs: line.cogs,
      frontInkColors: line.frontInkColors || [],
      backInkColors: line.backInkColors || [],
      otherInkColors: line.otherInkColors || [],
      printLocations: line.printLocations || [],
      inkNotes: line.inkNotes,
      files: line.files,
      ...line,
      isExpanded: true
    };
    setEditingLine(line);
    setEditedLineData(lineItem2Data);
  };

  const handleCloseEditLineModal = () => {
    setEditingLine(null);
    setEditedLineData(null);
  };

  const handleLineDataUpdate = (updatedData) => {
    setEditedLineData(updatedData);
  };

  const handleSaveEditedLine = async () => {
    if (!editingLine || !editedLineData) return;
    setSavingLine(true);
    try {
      const lineRef = doc(db, 'orderLines', editingLine.id);
      const totalQuantity = editedLineData.sizes
        ? Object.values(editedLineData.sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0)
        : 0;
      const mockupUrl = getMockupUrl(editedLineData);
      const persistableMockup = getPersistableMockupUrl(mockupUrl);
      const hasBrokenBlob = editedLineData.designFileUrl && String(editedLineData.designFileUrl).startsWith('blob:');
      const normalizedFiles = normalizeFilesForFirestore(editedLineData.files);

      const updateData = {
        designName: editedLineData.design || editedLineData.designName,
        garmentId: editedLineData.style,
        garmentColor: editedLineData.color,
        sizeType: editedLineData.sizeType,
        sizes: editedLineData.sizes || {},
        totalQuantity,
        pricePerPiece: parseFloat(editedLineData.price || editedLineData.pricePerPiece) || 0,
        cogs: parseFloat(editedLineData.cogs) || 0,
        type: editedLineData.type,
        frontInkColors: editedLineData.frontInkColors || [],
        backInkColors: editedLineData.backInkColors || [],
        otherInkColors: editedLineData.otherInkColors || [],
        printLocations: editedLineData.printLocations || [],
        inkNotes: editedLineData.inkNotes || null,
        updatedAt: serverTimestamp(),
        ...(normalizedFiles && { files: normalizedFiles }),
        ...(persistableMockup ? { designFileUrl: persistableMockup } : hasBrokenBlob ? { designFileUrl: null } : {})
      };

      await updateDoc(lineRef, updateData);

      const allInks = [
        ...(updateData.frontInkColors || []),
        ...(updateData.backInkColors || []),
        ...(updateData.otherInkColors || [])
      ].filter(Boolean);
      const updated = {
        ...editingLine,
        ...updateData,
        allInks,
        orderNumber: editingLine.orderNumber,
        customerName: editingLine.customerName,
        orderDate: editingLine.orderDate
      };
      setEnrichedLines(prev =>
        prev.map(l => (l.id === editingLine.id ? updated : l))
      );
      handleCloseEditLineModal();
    } catch (error) {
      console.error('Error saving line:', error);
      alert('Failed to save changes: ' + error.message);
    } finally {
      setSavingLine(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <HistoryContainer>
      <HistorySearchSection>
        <HistorySearchInputWrapper>
          <SearchInput
            type="text"
            placeholder="Search by ink name, client, or design..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </HistorySearchInputWrapper>
      </HistorySearchSection>

      {isLoading ? (
        <HistoryLoadingState>Loading job history...</HistoryLoadingState>
      ) : !searchTerm.trim() ? (
        <HistoryEmptyState>
          Type to search by ink name, client, or design name
        </HistoryEmptyState>
      ) : filteredResults.length > 0 ? (
        <>
          <HistoryResultsHeader>
            <HistoryResultsCount>
              Found {filteredResults.length} job{filteredResults.length !== 1 ? 's' : ''} matching "{searchTerm}"
            </HistoryResultsCount>
          </HistoryResultsHeader>
          <HistoryResultsList>
            {filteredResults.map((result, idx) => (
              <HistoryCard key={result.id || idx} onClick={() => openEditModal(result)}>
                <HistoryMockupContainer noImage={!getMockupUrl(result)}>
                  {getMockupUrl(result) && (
                    <HistoryMockupImage src={getMockupUrl(result)} alt={result.designName || result.design || 'Design'} />
                  )}
                </HistoryMockupContainer>
                <HistoryCardBody>
                  <HistoryCardHeader>
                    <HistoryCardTitle>
                      {result.customerName} — {result.designName || result.design || 'Unnamed Design'}
                    </HistoryCardTitle>
                    <HistoryCardDate>
                      {formatDate(result.orderDate)}
                    </HistoryCardDate>
                  </HistoryCardHeader>
                  <HistoryCardMeta>
                    <span>{result.garmentId || result.style || 'No style'}</span>
                    <span>{result.garmentColor || result.color || 'No color'}</span>
                  </HistoryCardMeta>
                  <HistoryCardInks>
                    {result.allInks.map((ink, i) => (
                      <HistoryInkChip 
                        key={i} 
                        isSearched={searchTerm.trim() && (ink || '').toLowerCase().includes(searchTerm.trim().toLowerCase())}
                      >
                        {ink}
                      </HistoryInkChip>
                    ))}
                  </HistoryCardInks>
                  {result.inkNotes ? (
                    <HistoryCardNotes>
                      <HistoryCardNotesLabel>Ink Notes</HistoryCardNotesLabel>
                      <HistoryCardNotesText>{result.inkNotes}</HistoryCardNotesText>
                    </HistoryCardNotes>
                  ) : (
                    <HistoryNoNotes>No ink notes for this line</HistoryNoNotes>
                  )}
                </HistoryCardBody>
              </HistoryCard>
            ))}
          </HistoryResultsList>
          {editingLine && editedLineData && (
            <EditLineModalOverlay onClick={handleCloseEditLineModal}>
              <EditLineModalBox onClick={(e) => e.stopPropagation()}>
                <EditLineModalHeader>
                  <EditLineModalTitle>✏️ Edit Line Item</EditLineModalTitle>
                  <EditLineCloseButton onClick={handleCloseEditLineModal}>×</EditLineCloseButton>
                </EditLineModalHeader>
                <EditLineModalContent>
                  <LineItem2
                    lineData={editedLineData}
                    onUpdate={handleLineDataUpdate}
                    isNew={false}
                    onDelete={() => {}}
                    onDuplicate={() => {}}
                    isExpanded={true}
                    lineId={editingLine.id}
                  />
                </EditLineModalContent>
                <EditLineModalActions>
                  <EditLineCancelButton onClick={handleCloseEditLineModal}>
                    Cancel
                  </EditLineCancelButton>
                  <EditLineSaveButton
                    onClick={handleSaveEditedLine}
                    disabled={savingLine}
                  >
                    {savingLine ? 'Saving...' : 'Save Changes'}
                  </EditLineSaveButton>
                </EditLineModalActions>
              </EditLineModalBox>
            </EditLineModalOverlay>
          )}
        </>
      ) : (
        <HistoryEmptyState>
          No jobs found matching "{searchTerm}"
        </HistoryEmptyState>
      )}
    </HistoryContainer>
  );
};

// Color Matcher Component
const ColorMatcher = ({ inks, onInkClick }) => {
  const [inputValue, setInputValue] = useState('');
  const [targetHex, setTargetHex] = useState('');
  const [detectedType, setDetectedType] = useState(null); // 'pantone' or 'hex'
  const [pantoneInfo, setPantoneInfo] = useState(null); // { code, hex }
  const [matches, setMatches] = useState([]);

  // Convert Pantone to Hex
  const pantoneToHex = (pantoneInput) => {
    // Normalize input - remove "PMS", "Pantone", extra spaces, and make uppercase
    let normalized = pantoneInput.toUpperCase()
      .replace(/PANTONE/gi, '')
      .replace(/PMS/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Try to find exact match in pantone-colors library
    // The library has names like "100 C", "185 C", etc.
    const pantone = pantoneColors.find(p => {
      const pantoneName = p.name.toUpperCase();
      // Try exact match
      if (pantoneName === normalized) return true;
      // Try with/without coating suffix (C, U, etc.)
      if (pantoneName.replace(/\s+[CU]$/, '') === normalized.replace(/\s+[CU]$/, '')) return true;
      // Try just the number
      const numberMatch = normalized.match(/^(\d+)/);
      if (numberMatch && pantoneName.startsWith(numberMatch[1] + ' ')) return true;
      return false;
    });

    return pantone ? { code: pantone.name, hex: pantone.hex } : null;
  };

  // Detect if input is Pantone or Hex
  const detectInputType = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Check if it looks like a hex code
    if (/^#?[0-9A-Fa-f]{3,6}$/.test(trimmed)) {
      return 'hex';
    }

    // Check if it looks like a Pantone code
    // Patterns: "185", "185 C", "PMS 185", "Pantone 185 C", etc.
    if (/^(pantone|pms)?\s*\d+\s*[CUcuMm]?$/i.test(trimmed)) {
      return 'pantone';
    }

    // Default to hex if it starts with #
    if (trimmed.startsWith('#')) {
      return 'hex';
    }

    // Otherwise, try pantone first
    return 'pantone';
  };

  // Handle input change
  const handleInputChange = (value) => {
    setInputValue(value);

    const inputType = detectInputType(value);
    setDetectedType(inputType);

    if (!value.trim()) {
      setTargetHex('');
      setPantoneInfo(null);
      return;
    }

    if (inputType === 'pantone') {
      // Try to convert Pantone to Hex
      const result = pantoneToHex(value);
      if (result) {
        setPantoneInfo(result);
        setTargetHex(result.hex);
      } else {
        setPantoneInfo(null);
        setTargetHex('');
      }
    } else {
      // Treat as hex
      setPantoneInfo(null);
      let normalized = value.trim();
      if (normalized && !normalized.startsWith('#')) {
        normalized = '#' + normalized;
      }
      setTargetHex(normalized);
    }
  };

  // Eyedropper functionality
  const handleEyedropper = async () => {
    if (!window.EyeDropper) {
      alert('Color picker is not supported in your browser. Please use Chrome, Edge, or another Chromium-based browser.');
      return;
    }

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      const hexColor = result.sRGBHex.toUpperCase();
      setInputValue(hexColor);
      setDetectedType('hex');
      setPantoneInfo(null);
      setTargetHex(hexColor);
    } catch (error) {
      // User cancelled or error occurred
      console.log('Eyedropper cancelled or error:', error);
    }
  };

  // Calculate Delta E 2000 (CIEDE2000) - industry standard for color difference
  const calculateDeltaE = (hex1, hex2) => {
    try {
      const color1 = chroma(hex1);
      const color2 = chroma(hex2);
      return chroma.deltaE(color1, color2);
    } catch (error) {
      return 999; // Return high value for invalid colors
    }
  };

  // Get quality label based on Delta E
  const getQualityLabel = (deltaE) => {
    if (deltaE < 1) return 'Perfect Match';
    if (deltaE < 5) return 'Excellent Match';
    if (deltaE < 15) return 'Good Match';
    if (deltaE < 30) return 'Moderate Match';
    return 'Poor Match';
  };

  // Calculate matches whenever target hex changes
  useEffect(() => {
    if (!targetHex || targetHex.length < 4) {
      setMatches([]);
      return;
    }

    try {
      // Validate hex
      chroma(targetHex);
      
      // Calculate Delta E for all inks with hex codes
      const validInks = inks.filter(ink => ink.hexCode);
      const matchesWithDeltaE = validInks.map(ink => ({
        ...ink,
        deltaE: calculateDeltaE(targetHex, ink.hexCode)
      }));

      // Sort by Delta E (closest first)
      matchesWithDeltaE.sort((a, b) => a.deltaE - b.deltaE);

      // Take top 12 matches
      setMatches(matchesWithDeltaE.slice(0, 12));
    } catch (error) {
      // Invalid hex, clear matches
      setMatches([]);
    }
  }, [targetHex, inks]);

  const isValidHex = (hex) => {
    try {
      chroma(hex);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <MatcherContainer>
      <MatcherInputSection>
        <MatcherTitle>Color Matcher</MatcherTitle>
        <HexInputGroup>
          <HexInputWrapper>
            <Label>Enter Pantone or Hex Code (or Use Color Picker)</Label>
            <Input
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="PMS 185 C, 185, #FF6B35, or FF6B35"
              style={{ textTransform: 'uppercase', fontSize: '18px' }}
            />
          </HexInputWrapper>
          <EyedropperButton 
            onClick={handleEyedropper}
            title="Pick a color from anywhere on your screen"
            disabled={!window.EyeDropper}
          >
            ⊙ Pick
          </EyedropperButton>
          <ColorPreview color={isValidHex(targetHex) ? targetHex : '#333'} />
        </HexInputGroup>
        
        {/* Show Pantone conversion info */}
        {pantoneInfo && (
          <div style={{ 
            color: '#62E07D', 
            fontSize: '13px', 
            marginTop: '12px',
            fontFamily: 'Montserrat',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span>✓ Pantone {pantoneInfo.code}</span>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>→</span>
            <span>{pantoneInfo.hex}</span>
          </div>
        )}
        
        {/* Show error if Pantone not found */}
        {detectedType === 'pantone' && inputValue && !pantoneInfo && (
          <div style={{ 
            color: '#FF6B6B', 
            fontSize: '12px', 
            marginTop: '12px',
            fontFamily: 'Montserrat',
            textTransform: 'uppercase'
          }}>
            Pantone color not found. Try "185 C" or enter a hex code.
          </div>
        )}
        
        {/* Show error if hex is invalid */}
        {detectedType === 'hex' && inputValue && !isValidHex(targetHex) && (
          <div style={{ 
            color: '#FF6B6B', 
            fontSize: '12px', 
            marginTop: '12px',
            fontFamily: 'Montserrat',
            textTransform: 'uppercase'
          }}>
            Invalid hex code. Use format: #RRGGBB
          </div>
        )}
      </MatcherInputSection>

      {matches.length > 0 ? (
        <>
          <div style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: '14px',
            fontFamily: 'Montserrat',
            textTransform: 'uppercase',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Showing Top {matches.length} Closest Matches
          </div>
          <MatchResultsGrid>
            {matches.map(ink => (
              <MatchCard 
                key={ink.id} 
                deltaE={ink.deltaE}
                onClick={() => onInkClick(ink)}
              >
                <MatchCardHeader>
                  <MatchColorSwatch color={ink.hexCode} />
                  <MatchInfo>
                    <MatchName>{ink.name}</MatchName>
                    <MatchDetails>
                      {ink.hexCode}
                      {ink.pantoneNumber && (
                        <div style={{ marginTop: '4px' }}>{ink.pantoneNumber}</div>
                      )}
                    </MatchDetails>
                  </MatchInfo>
                </MatchCardHeader>
                <DeltaEBadge value={ink.deltaE}>
                  ΔE: {ink.deltaE.toFixed(1)}
                </DeltaEBadge>
                <MatchQuality deltaE={ink.deltaE}>
                  {getQualityLabel(ink.deltaE)}
                </MatchQuality>
              </MatchCard>
            ))}
          </MatchResultsGrid>
        </>
      ) : (
        <NoResults>
          {inputValue 
            ? (isValidHex(targetHex) ? 'No inks with color data found' : 'Enter a valid Pantone or hex code to find matches')
            : 'Enter a Pantone code or hex code above to find closest matching inks'}
        </NoResults>
      )}

      <div style={{
        marginTop: '40px',
        padding: '20px',
        background: 'rgba(98, 224, 125, 0.1)',
        border: '1px solid rgba(98, 224, 125, 0.3)',
        borderRadius: '8px',
        maxWidth: '800px',
        width: '100%'
      }}>
        <div style={{
          color: 'white',
          fontFamily: 'Montserrat',
          fontSize: '12px',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginBottom: '12px'
        }}>
          Delta E (ΔE) Scale Guide
        </div>
        <div style={{
          color: 'rgba(255,255,255,0.7)',
          fontFamily: 'Montserrat',
          fontSize: '11px',
          textTransform: 'uppercase',
          lineHeight: 1.8
        }}>
          <div>• <strong style={{ color: '#62E07D' }}>ΔE &lt; 1:</strong> Perfect Match (imperceptible difference)</div>
          <div>• <strong style={{ color: '#62E07D' }}>ΔE 1-5:</strong> Excellent Match (slight difference, very close)</div>
          <div>• <strong style={{ color: '#FFD700' }}>ΔE 5-15:</strong> Good Match (noticeable but acceptable)</div>
          <div>• <strong style={{ color: '#FFA500' }}>ΔE 15-30:</strong> Moderate Match (clearly different)</div>
          <div>• <strong style={{ color: '#FF6B6B' }}>ΔE &gt; 30:</strong> Poor Match (significantly different)</div>
        </div>
      </div>
    </MatcherContainer>
  );
};

// Color Wheel Component
const ColorWheel = ({ inks, onInkHover, onInkClick, searchTerm }) => {
  const size = 1000; // Larger wheel for 200+ inks
  const center = size / 2;
  const wheelRadius = size * 0.42; // Slightly larger radius
  const [tooltipInk, setTooltipInk] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Convert hex to HSL for positioning
  const hexToHSL = (hex) => {
    if (!hex) return { h: 0, s: 0, l: 50 };
    try {
      const color = chroma(hex);
      const [h, s, l] = color.hsl();
      return {
        h: isNaN(h) ? 0 : h,  // Hue 0-360
        s: isNaN(s) ? 0 : s,  // Saturation 0-1
        l: isNaN(l) ? 0.5 : l // Lightness 0-1
      };
    } catch (error) {
      return { h: 0, s: 0, l: 50 };
    }
  };

  // Convert HSL polar coordinates to XY cartesian
  const polarToCartesian = (hue, saturation) => {
    const angle = (hue - 90) * (Math.PI / 180); // -90 to start at top
    const radius = saturation * wheelRadius;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  };

  // Filter inks based on search
  const filteredInks = inks.filter(ink =>
    ink.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Only show inks with valid hex codes
  const validInks = filteredInks.filter(ink => ink.hexCode);

  // Calculate color family distribution
  const colorStats = useMemo(() => {
    const stats = {
      reds: 0,      // 330-30°
      oranges: 0,   // 30-60°
      yellows: 0,   // 60-90°
      greens: 0,    // 90-150°
      cyans: 0,     // 150-210°
      blues: 0,     // 210-270°
      purples: 0,   // 270-330°
      neutrals: 0   // Low saturation
    };
    
    validInks.forEach(ink => {
      const hsl = hexToHSL(ink.hexCode);
      
      if (hsl.s < 0.15) {
        stats.neutrals++;
      } else {
        const h = hsl.h;
        if (h >= 330 || h < 30) stats.reds++;
        else if (h >= 30 && h < 60) stats.oranges++;
        else if (h >= 60 && h < 90) stats.yellows++;
        else if (h >= 90 && h < 150) stats.greens++;
        else if (h >= 150 && h < 210) stats.cyans++;
        else if (h >= 210 && h < 270) stats.blues++;
        else if (h >= 270 && h < 330) stats.purples++;
      }
    });
    
    return stats;
  }, [validInks]);

  const handleMouseEnter = (ink, dotX, dotY) => {
    // Position tooltip above the dot
    setTooltipPos({ x: dotX, y: dotY - 20 }); // 20px above the dot
    setTooltipInk(ink);
    onInkHover(ink);
  };

  const handleMouseLeave = () => {
    setTooltipInk(null);
    onInkHover(null);
  };

  return (
    <WheelContainer>
      <svg width={size} height={size} style={{ background: '#0a0a0a', borderRadius: '50%' }}>
        {/* Draw color wheel background */}
        <defs>
          <radialGradient id="wheelGradient">
            <stop offset="0%" stopColor="white" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          
          {/* Create color wheel with conic gradient simulation */}
          {Array.from({ length: 360 }).map((_, i) => {
            const hue = i;
            const color = chroma.hsl(hue, 1, 0.5).hex();
            return (
              <React.Fragment key={i}>
                <line
                  x1={center}
                  y1={center}
                  x2={center + wheelRadius * Math.cos((hue - 90) * Math.PI / 180)}
                  y2={center + wheelRadius * Math.sin((hue - 90) * Math.PI / 180)}
                  stroke={color}
                  strokeWidth={wheelRadius * 2 / 360}
                  opacity={0.3}
                />
              </React.Fragment>
            );
          })}
        </defs>

        {/* Center white circle (low saturation area) */}
        <circle
          cx={center}
          cy={center}
          r={wheelRadius * 0.15}
          fill="white"
          opacity={0.1}
        />

        {/* Grid lines for reference */}
        <circle cx={center} cy={center} r={wheelRadius * 0.33} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
        <circle cx={center} cy={center} r={wheelRadius * 0.66} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
        <circle cx={center} cy={center} r={wheelRadius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={2} />

        {/* Plot ink dots */}
        {validInks.map(ink => {
          const hsl = hexToHSL(ink.hexCode);
          const { x, y } = polarToCartesian(hsl.h, hsl.s);
          
          // Dynamic dot sizing based on total ink count (50% larger for visual density)
          const inkCount = validInks.length;
          const baseDotSize = inkCount > 150 ? 7.5 : inkCount > 100 ? 9 : 10.5;
          
          // Scale slightly based on lightness (mid-tones slightly larger)
          const lightnessScale = 0.8 + (1 - Math.abs(hsl.l - 0.5)) * 0.4; // 0.8 to 1.2
          const dotSize = baseDotSize * lightnessScale;
          
          const isHovered = tooltipInk?.id === ink.id;
          
          return (
            <g key={ink.id}>
              {/* Outer glow - only show on hover */}
              {isHovered && (
                <circle
                  cx={x}
                  cy={y}
                  r={dotSize + 8}
                  fill={ink.hexCode}
                  opacity={0.3}
                />
              )}
              {/* Main dot */}
              <circle
                cx={x}
                cy={y}
                r={dotSize}
                fill={ink.hexCode}
                stroke={isHovered ? '#62E07D' : 'white'}
                strokeWidth={isHovered ? 3 : 1.5}
                style={{ 
                  cursor: 'pointer', 
                  transition: 'all 0.2s',
                  transform: isHovered ? 'scale(1.5)' : 'scale(1)',
                  transformOrigin: `${x}px ${y}px`
                }}
                onMouseEnter={() => handleMouseEnter(ink, x, y)}
                onMouseLeave={handleMouseLeave}
                onClick={() => onInkClick(ink)}
              />
            </g>
          );
        })}

        {/* Center label */}
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dy="0.3em"
          fill="rgba(255,255,255,0.3)"
          fontSize="12"
          fontFamily="Montserrat"
          fontWeight="600"
        >
          NEUTRAL
        </text>
      </svg>

      {/* Tooltip */}
      {tooltipInk && (
        <Tooltip style={{ 
          left: `${tooltipPos.x}px`, 
          top: `${tooltipPos.y}px`,
          transform: 'translate(-50%, -100%)' // Center horizontally, position above dot
        }}>
          {tooltipInk.name}
          {tooltipInk.pantoneNumber && (
            <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
              {tooltipInk.pantoneNumber}
            </div>
          )}
        </Tooltip>
      )}

      {/* Legend */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        color: 'rgba(255,255,255,0.6)',
        fontSize: '12px',
        fontFamily: 'Montserrat',
        textTransform: 'uppercase',
        textAlign: 'center',
        fontWeight: 600
      }}>
        <div>Showing {validInks.length} inks with color data</div>
        {searchTerm && <div style={{ marginTop: '4px', color: '#62E07D' }}>Filtered results</div>}
        <div style={{ marginTop: '8px', fontSize: '10px', opacity: 0.7 }}>
          Click any dot to edit • Hover to see name
        </div>
      </div>

      {/* Color Stats Panel - Hidden for now */}
      {/* {false && <ColorStatsPanel>...</ColorStatsPanel>} */}
    </WheelContainer>
  );
};

function InkInventory() {
  const [inks, setInks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingInk, setEditingInk] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', hexCode: '', pantoneNumber: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('list'); // 'list', 'wheel', 'matcher', or 'history'
  const [hoveredInk, setHoveredInk] = useState(null);

  // Convert hex to LCH (Lightness, Chroma, Hue) using chroma-js
  // LCH is perceptually uniform - better for color sorting
  const hexToLCH = (hex) => {
    if (!hex) return { l: 0, c: 0, h: 0 };
    
    try {
      const color = chroma(hex);
      const [l, c, h] = color.lch();
      
      return {
        l: l || 0,           // Lightness (0-100)
        c: c || 0,           // Chroma (0-~130, saturation/intensity)
        h: isNaN(h) ? 0 : h  // Hue (0-360 degrees)
      };
    } catch (error) {
      console.error('Error converting hex to LCH:', hex, error);
      return { l: 0, c: 0, h: 0 };
    }
  };

  // Sort function
  const sortInks = (inksToSort, column, direction) => {
    const sorted = [...inksToSort].sort((a, b) => {
      let aVal, bVal;
      
      switch (column) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          return direction === 'asc' 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
          
        case 'hexCode':
          // LCH-based perceptual color sorting
          // LCH (Lightness, Chroma, Hue) is perceptually uniform
          const CHROMA_NEUTRAL = 10; // Threshold for neutral colors (low chroma)
          
          const A = hexToLCH(a.hexCode);
          const B = hexToLCH(b.hexCode);
          
          const aNeutral = A.c < CHROMA_NEUTRAL;
          const bNeutral = B.c < CHROMA_NEUTRAL;
          
          // Separate neutrals from vivid colors
          if (aNeutral !== bNeutral) {
            return direction === 'asc' 
              ? (aNeutral ? 1 : -1)  // Neutrals at end
              : (aNeutral ? -1 : 1);  // Neutrals at start
          }
          
          // Sort neutrals by lightness only (blacks -> grays -> whites)
          if (aNeutral && bNeutral) {
            return direction === 'asc' 
              ? A.l - B.l
              : B.l - A.l;
          }
          
          // Sort vivid colors by: Hue → Chroma → Lightness
          // This creates a smooth perceptual progression
          
          // Primary sort: Hue (color spectrum)
          if (Math.abs(A.h - B.h) > 1) {  // Allow 1 degree tolerance
            return direction === 'asc' 
              ? A.h - B.h
              : B.h - A.h;
          }
          
          // Secondary sort: Chroma (vivid → muted)
          if (Math.abs(A.c - B.c) > 1) {  // Allow small tolerance
            return direction === 'asc' 
              ? B.c - A.c  // Higher chroma first (more vivid)
              : A.c - B.c;
          }
          
          // Tertiary sort: Lightness (dark → light)
          return direction === 'asc' 
            ? A.l - B.l
            : B.l - A.l;
          
        case 'pantoneNumber':
          aVal = (a.pantoneNumber || '').toLowerCase();
          bVal = (b.pantoneNumber || '').toLowerCase();
          return direction === 'asc' 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
          
        default:
          return 0;
      }
    });
    
    return sorted;
  };

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter and sort inks
  const filteredInks = useMemo(() => {
    const filtered = inks.filter(ink => 
      ink.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return sortInks(filtered, sortColumn, sortDirection);
  }, [inks, searchTerm, sortColumn, sortDirection]);

  // Fetch inks from Firestore
  const fetchInks = async () => {
    try {
      setIsLoading(true);
      const inksRef = collection(db, 'inks');
      const snapshot = await getDocs(inksRef);
      const inksList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort alphabetically by name
      inksList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setInks(inksList);
    } catch (error) {
      console.error('Error fetching inks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch inks on component mount
  useEffect(() => {
    fetchInks();
  }, []);

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingInk(null);
    setEditForm({ name: '', hexCode: '', pantoneNumber: '' });
  };

  const handleEdit = (ink) => {
    setEditingInk(ink);
    setIsAddingNew(false);
    setEditForm({
      name: ink.name || '',
      hexCode: ink.hexCode || '',
      pantoneNumber: ink.pantoneNumber || ''
    });
  };

  // Eyedropper functionality for modal
  const handleModalEyedropper = async () => {
    if (!window.EyeDropper) {
      alert('Color picker is not supported in your browser. Please use Chrome, Edge, or another Chromium-based browser.');
      return;
    }

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      const hexColor = result.sRGBHex.toUpperCase();
      setEditForm({...editForm, hexCode: hexColor});
    } catch (error) {
      // User cancelled or error occurred
      console.log('Eyedropper cancelled or error:', error);
    }
  };

  const handleSave = async () => {
    if (!editForm.name || editForm.name.trim() === '') {
      alert('Ink name is required');
      return;
    }

    // Validate hex code if provided
    const hexCode = editForm.hexCode?.trim() || '';
    if (hexCode && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexCode)) {
      alert('Invalid hex code format. Please use format #RRGGBB or #RGB');
      return;
    }

    try {
      const updateData = {
        name: editForm.name.trim(),
        hexCode: hexCode || null,
        pantoneNumber: editForm.pantoneNumber?.trim() || null,
        updatedAt: serverTimestamp()
      };

      if (editingInk) {
        // Update existing ink
        const inkRef = doc(db, 'inks', editingInk.id);
        await updateDoc(inkRef, updateData);
      } else {
        // Add new ink
        await addDoc(collection(db, 'inks'), {
          ...updateData,
          createdAt: serverTimestamp()
        });
      }
      
      await fetchInks();
      setEditingInk(null);
      setIsAddingNew(false);
      setEditForm({ name: '', hexCode: '', pantoneNumber: '' });
    } catch (error) {
      console.error('Error saving ink:', error);
      alert('Error saving ink. Please try again.');
    }
  };

  const handleDelete = async (inkId) => {
    if (!window.confirm('Are you sure you want to delete this ink?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'inks', inkId));
      await fetchInks();
    } catch (error) {
      console.error('Error deleting ink:', error);
      alert('Error deleting ink. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setEditingInk(null);
    setIsAddingNew(false);
    setEditForm({ name: '', hexCode: '', pantoneNumber: '' });
  };

  // Helper to normalize hex code (add # if missing, expand shorthand)
  const normalizeHexCode = (hex) => {
    if (!hex) return '';
    let normalized = hex.trim();
    if (!normalized.startsWith('#')) {
      normalized = '#' + normalized;
    }
    // Expand shorthand hex (e.g., #fff -> #ffffff)
    if (normalized.length === 4) {
      normalized = '#' + normalized[1] + normalized[1] + normalized[2] + normalized[2] + normalized[3] + normalized[3];
    }
    return normalized.toUpperCase();
  };

  // Get display color from hex code or default
  const getDisplayColor = (hexCode) => {
    if (!hexCode) return '#cccccc';
    const normalized = normalizeHexCode(hexCode);
    return normalized.length === 7 ? normalized : '#cccccc';
  };

  return (
    <PageContainer>
      <Header>
        <h1>Ink Inventory</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <ViewToggle>
            <ViewButton 
              active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              📋 List
            </ViewButton>
            <ViewButton 
              active={viewMode === 'wheel'} 
              onClick={() => setViewMode('wheel')}
            >
              🎨 Wheel
            </ViewButton>
            <ViewButton 
              active={viewMode === 'matcher'} 
              onClick={() => setViewMode('matcher')}
            >
              🔍 Matcher
            </ViewButton>
            <ViewButton 
              active={viewMode === 'history'} 
              onClick={() => setViewMode('history')}
            >
              📜 History
            </ViewButton>
          </ViewToggle>
          <Button onClick={handleAddNew}>
            Add New Ink
          </Button>
        </div>
      </Header>

      {(viewMode === 'list' || viewMode === 'wheel') && (
        <FiltersContainer>
          <SearchInput
            type="text"
            placeholder="SEARCH INKS..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </FiltersContainer>
      )}

      
      {isLoading ? (
        <div>Loading inks...</div>
      ) : viewMode === 'history' ? (
        <InkHistory inks={inks} />
      ) : viewMode === 'matcher' ? (
        <ColorMatcher 
          inks={inks}
          onInkClick={handleEdit}
        />
      ) : viewMode === 'wheel' ? (
        <ColorWheel 
          inks={inks}
          onInkHover={setHoveredInk}
          onInkClick={handleEdit}
          searchTerm={searchTerm}
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Color</Th>
              <Th 
                sortable 
                active={sortColumn === 'name'}
                onClick={() => handleSort('name')}
              >
                Ink Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th 
                sortable 
                active={sortColumn === 'hexCode'}
                onClick={() => handleSort('hexCode')}
              >
                Hex Code {sortColumn === 'hexCode' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th 
                sortable 
                active={sortColumn === 'pantoneNumber'}
                onClick={() => handleSort('pantoneNumber')}
              >
                Pantone {sortColumn === 'pantoneNumber' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredInks.length === 0 ? (
              <tr>
                <Td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                  {searchTerm ? 'No inks found matching your search' : 'No inks in inventory. Click "Add New Ink" to get started.'}
                </Td>
              </tr>
            ) : (
              filteredInks.map((ink) => (
                <Tr key={ink.id}>
                  <Td>
                    <ColorSwatch color={getDisplayColor(ink.hexCode)} />
                  </Td>
                  <Td>{ink.name}</Td>
                  <Td>{ink.hexCode || '-'}</Td>
                  <Td>{ink.pantoneNumber || '-'}</Td>
                  <Td>
                    <EditButton onClick={() => handleEdit(ink)}>
                      Edit
                    </EditButton>
                    <DeleteButton onClick={() => handleDelete(ink.id)}>
                      Delete
                    </DeleteButton>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {(editingInk || isAddingNew) && (
        <Modal onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2>{isAddingNew ? 'Add New Ink' : 'Edit Ink'}</h2>
            
            <FormGroup>
              <Label>Ink Name *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                placeholder="e.g., Fire Hydrant Red"
                autoFocus
              />
            </FormGroup>

            <FormGroup>
              <Label>Hex Code (or Use Color Picker)</Label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <Input
                    value={editForm.hexCode}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Auto-add # if user types without it
                      if (value && !value.startsWith('#') && /^[0-9A-Fa-f]+$/.test(value.replace('#', ''))) {
                        value = '#' + value.replace('#', '');
                      }
                      setEditForm({...editForm, hexCode: value});
                    }}
                    placeholder="#FF0000 or FF0000"
                    maxLength={7}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <EyedropperButton 
                  onClick={handleModalEyedropper}
                  title="Pick a color from anywhere on your screen"
                  disabled={!window.EyeDropper}
                  style={{ marginTop: '0' }}
                >
                  ⊙ Pick
                </EyedropperButton>
              </div>
              {editForm.hexCode && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <ColorSwatch color={getDisplayColor(editForm.hexCode)} style={{ width: '60px', height: '60px' }} />
                  <span style={{ color: '#fff', fontSize: '14px' }}>
                    {normalizeHexCode(editForm.hexCode) || editForm.hexCode}
                  </span>
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Pantone Number</Label>
              <Input
                value={editForm.pantoneNumber}
                onChange={(e) => setEditForm({...editForm, pantoneNumber: e.target.value.toUpperCase()})}
                placeholder="e.g., PMS 185 C"
                style={{ textTransform: 'uppercase' }}
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleCloseModal}>Cancel</CancelButton>
              <SaveButton onClick={handleSave}>Save</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
}

export default InkInventory;



